import { AnyFunc, createEventEmitter, eventObject, Logger, PlatformUtil } from '@quickplay/foundation';
import {
  Player,
  PlayerContext,
  PLAYBACK_STATES,
  AdPlaybackPolicy,
  PlaybackFastForwardMode,
  PlaybackRewindMode,
  PlaybackInterruptedMode,
  PlaybackAutoSeekMode,
  PlaybackRepeatedMode,
  AdContentTimePosition,
  AdBreakInfo,
  // Ad,
  AdEvent,
  AdInfo,
} from '@quickplay/player-interface';
import { Avail } from '../../model/AdMetadata';
import { MediatailorPlaybackPolicyHandler } from '../policy/AdPlaybackPolicyHandler';
import { getSeekPositionFor } from '../policy/AdPolicyUtil';
import { MediatailorAdHandler } from '../../model/MediatailorAdHandler';
import { AdPlaybackState, DatazoomMediaTailorAd, DatazoomMediaTailorAdsResponse } from '../../model/DatazoomAdMetaData';
import { DatazoomFactory } from './DatazoomFactory';
import { AdQuartileTracker } from '../AdQuartileTracker';

export class DatazoomMediatailorAdHandlerImpl implements MediatailorAdHandler {
  private readonly player_: Player;
  private readonly logger_?: Logger;
  private readonly eventEmitter = createEventEmitter();

  private readonly adMetadata_?: DatazoomMediaTailorAdsResponse;
  private avails: Avail[] = [];

  private playerContext_: PlayerContext = PlayerContext.MAIN;
  private currentAvail_?: Avail;
  private currentAdBreakInfo_?: AdBreakInfo;
  private currentAdInfo?: AdInfo;
  private readonly session: any;
  private adBreakTotalAds = 0;

  private adPolicyHandler: MediatailorPlaybackPolicyHandler | undefined = undefined;

  private readonly sessionUiEvent = DatazoomFactory.getSDK().mediatailor.SessionUiEvent;
  private prerollLoadTimeInMS = 0;
  private playerLoadStartTimeInMS = 0;
  private readonly MAX_RETRIES_FOR_TRACKING_URL = 2;
  private trackingInfoTries = 0;
  private readonly quartileTracker: AdQuartileTracker;

  private readonly defaultAdPlaybackPolicy: AdPlaybackPolicy = {
    fastForwardMode: PlaybackFastForwardMode.TRICK_FW_PLAY_LAST,
    rewindMode: PlaybackRewindMode.TRICK_RW_SKIP_ALL,
    autoSeekMode: PlaybackAutoSeekMode.AUTO_SEEK_SKIP_ALL,
    repeatMode: PlaybackRepeatedMode.REPEAT_PLAY_ONCE,
    interruptMode: PlaybackInterruptedMode.INTERRUPT_RESUME,
  };

  constructor(
    player: Player,
    mediatailorAdHandlerMetadata: {
      adMetadata?: DatazoomMediaTailorAdsResponse;
      adPlaybackPolicy?: AdPlaybackPolicy;
      session: any;
    },
    logger: Logger,
  ) {
    this.player_ = player;
    this.logger_ = logger;
    this.quartileTracker = new AdQuartileTracker(this.eventEmitter);
    this.session = mediatailorAdHandlerMetadata.session;
    this.adMetadata_ = mediatailorAdHandlerMetadata?.adMetadata;

    this.adPolicyHandler = new MediatailorPlaybackPolicyHandler(
      player,
      mediatailorAdHandlerMetadata.adPlaybackPolicy ?? this.defaultAdPlaybackPolicy,
      logger,
    );

    if (this.adMetadata_) {
      this.updateAdMetadata(this.adMetadata_);
    }
    this.initialize(); // NOSONAR
  }

  adClicked(_adUrl: string): void {
    this.session.onAdClickthrough();
  }

  getAllAdBreaks(): AdBreakInfo[] {
    return this.avails.map((avail, index) => this.createAdBreakInfo(avail, index));
  }
  private updateAdMetadata(adMetadata_: DatazoomMediaTailorAdsResponse): void {
    let avails = this.convertToAvails(adMetadata_);
    if (!avails) {
      return;
    }

    // Filter out avails with invalid duration
    avails = avails.filter(
      avail => avail.durationInSeconds !== undefined && avail.durationInSeconds !== null && avail.durationInSeconds > 0,
    );

    const newAvailIds = new Set(avails.map(avail => avail.availId));

    const remainingAvails = this.avails.filter(avail => !newAvailIds.has(avail.availId));

    this.avails = [...remainingAvails, ...avails];
  }

  subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.subscribe(eventName, callback);
  }

  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.unsubscribe(eventName, callback);
  }

  private convertToAvails(adMetadata: DatazoomMediaTailorAdsResponse): Avail[] {
    if (!adMetadata?.length) {
      return [];
    }

    const availMap = new Map<string, Avail>();

    adMetadata.forEach(mtAd => {
      if (!mtAd.availId) {
        return;
      }

      let avail = availMap.get(mtAd.availId);

      if (!avail) {
        avail = {
          availId: mtAd.availId,
          startTime: mtAd.availStart,
          startTimeInSeconds: mtAd.availStart,
          availProgramDateTime: mtAd.startDate,
          duration: mtAd.availDuration,
          durationInSeconds: mtAd.availDuration,
          ads: [],
        };

        availMap.set(mtAd.availId, avail);
      }

      const adStart = mtAd.start;
      const adDuration = mtAd.duration;

      this.logger_?.log('[DatazoomMediatailorPlayerAd] Processing ad:', mtAd);
      avail.ads!.push({
        adId: mtAd.adId,
        adParameters: mtAd.adParameters ?? '',
        adProgramDateTime: mtAd.startDate ? new Date(mtAd.startDate * 1000).toISOString() : '',
        adSystem: mtAd.adSystem ?? '',
        adTitle: mtAd.adTitle ?? '',
        adVerifications: mtAd.adVerifications ?? [],
        creativeId: mtAd.creativeId ?? '',
        creativeSequence: mtAd.creativeSequence ?? '',
        duration: (adDuration * 1000).toString(),
        durationInSeconds: adDuration,
        startTime: (adStart * 1000).toString(),
        startTimeInSeconds: adStart,
        vastAdId: mtAd.vastAdId ?? null,
        skipOffset: mtAd.skipOffset?.toString() ?? null,
        trackingEvents: (mtAd.trackingEvents ?? []).map(evt => ({
          beaconUrls: evt.beaconUrls ?? [],
          duration: (evt.durationInSeconds * 1000).toString(),
          durationInSeconds: evt.durationInSeconds,
          eventId: evt.eventId,
          eventProgramDateTime: null,
          eventType: evt.eventType,
          startTime: (evt.startTimeInSeconds * 1000).toString(),
          endTimeInSeconds: evt.startTimeInSeconds + evt.durationInSeconds,
        })),
        endTimeInSeconds: adStart + adDuration,
      });
    });

    this.logger_?.log('[DatazoomMediatailorPlayerAd] Converted ad metadata to avails:', Array.from(availMap.values()));
    return Array.from(availMap.values());
  }

  currentTime(): number {
    if (!this.player_) {
      return 0;
    }
    return this.player_.isLive
      ? this.player_.currentTime
      : (this.adPolicyHandler?.getCurrentTime(this.currentAvail_) ?? this.player_.currentTime);
  }

  duration(): number {
    if (!this.player_?.duration || Number.isNaN(this.player_.duration)) {
      return 0;
    }
    if (this.player_.isLive) {
      return this.player_.duration;
    }
    return this.player_.duration - this.getTotalAdvertsBreaksDuration(this.avails ?? []);
  }

  seek(seekPoint: number): void {
    if (!this.player_) {
      return;
    }

    if (!this.adPolicyHandler) {
      this.player_.seek(seekPoint);
      return;
    }
    this.player_.seek(
      this.adPolicyHandler.willSeekTo(getSeekPositionFor(seekPoint, this.avails ?? [], this.player_.duration)) ?? 0,
    );
  }

  getInitialPlaybackTimeWithAds(): number {
    if (!this.player_ || this.player_.isLive || !this.player_.playbackOptions?.initialPlaybackTime) {
      return 0;
    }
    const position = this.player_.playbackOptions.initialPlaybackTime;
    let initialPlaybackTimeWithAds = getSeekPositionFor(position, this.avails ?? [], this.player_.duration);
    initialPlaybackTimeWithAds =
      this.adPolicyHandler?.willSeekTo(initialPlaybackTimeWithAds, true) ?? initialPlaybackTimeWithAds;

    return initialPlaybackTimeWithAds;
  }

  getCuePointsRelativeToContentTime(): number[] {
    if (!this.player_) {
      this.logger_?.warn('[AD] Player is not available for cue point calculation.');
      return [];
    }

    if (this.avails.length === 0) {
      this.logger_?.warn('[AD] No ad breaks available to calculate cue points.');
      return [];
    }

    const watchedAdBreaks = this.adPolicyHandler?.getWatchedAdBreaks() ?? [];
    const watchedAdBreakIds = new Set(watchedAdBreaks.map(avail => avail.availId));

    const unwatchedAvails = this.avails.filter(avail => {
      return !watchedAdBreakIds.has(avail.availId);
    });

    if (unwatchedAvails.length === 0) {
      return [];
    }

    const cuePoints = unwatchedAvails.map(adBreak => {
      const totalAdTime = this.avails
        .slice(0, this.avails.indexOf(adBreak))
        .reduce((sum, avail) => sum + (avail?.durationInSeconds ?? 0), 0);

      const relativeContentTime = (adBreak.startTimeInSeconds ?? 0) - totalAdTime;

      if ((adBreak?.startTimeInSeconds ?? 0) + (adBreak?.durationInSeconds ?? 0) === this.player_.duration) {
        return adBreak.startTimeInSeconds ?? 0;
      }

      return Math.max(relativeContentTime, 0);
    });

    return [...new Set(cuePoints)];
  }

  playerContext(): PlayerContext {
    return this.playerContext_;
  }

  getPrerollLoadTimeInMS(): number | undefined {
    return this.prerollLoadTimeInMS;
  }

  private async initialize(): Promise<void> {
    try {
      this.subscribeToPlayerEvents();
      this.session && this.observeAdEvents(this.session);
    } catch (error) {
      this.logger_?.error('[AD] Failed to initialize MediaTailor handler:', error);
    }
  }
  private readonly onAdProgress = (event: any): void => {
    this.reportAdProgress(event.detail);
  };

  private readonly onAdStart = (event: any): void => {
    this.handleAdBreakStart(event.detail);
  };

  private readonly onAdEnd = (event: any): void => {
    this.handleAdBreakEnd(event.detail);
  };

  private readonly onAdClick = (_event: any): void => {
    this.eventEmitter.publish(AdEvent.ADVERT_CLICK, this.currentAdInfo?.vastProperties?.videoClickThroughURL);
  };

  private readonly onTrackingInfo = (_event: any) => {
    if (this.player_.isLive) {
      return;
    }

    // this is for safari browser where tracking info response can be delayed and cause ad metadata to not be set before ad start event is fired, so we allow retrying for a few times before giving up
    this.trackingInfoTries++;
    const ads = this.session.getAllAds?.();

    // Ads not ready yet → allow retry
    if ((!ads || ads.length === 0) && this.trackingInfoTries <= this.MAX_RETRIES_FOR_TRACKING_URL) {
      return;
    }
    this.updateAdMetadata(ads);
    this.adPolicyHandler?.setAdBreaks(this.avails);
    this.initializeWithInitialPlaybackTime();
    this.eventEmitter.publish(AdEvent.ADVERT_CUEPOINTS, this.getCuePointsRelativeToContentTime());

    // Stop future updates
    this.session.stopUpdatingAdTrackingInfo?.();

    // Cleanup listener (one-shot)
    this.cleanupTrackingInfoListener();
  };

  private observeAdEvents(session: any): void {
    session.addEventListener(this.sessionUiEvent.AD_PROGRESS, this.onAdProgress);
    session.addEventListener(this.sessionUiEvent.AD_START, this.onAdStart);
    session.addEventListener(this.sessionUiEvent.AD_END, this.onAdEnd);
    session.addEventListener(this.sessionUiEvent.AD_CLICK, this.onAdClick);
    session.addEventListener(this.sessionUiEvent.AD_TRACKING_INFO_RESPONSE, this.onTrackingInfo);
  }

  private removeObserveAdEvents(session: any): void {
    session.removeEventListener(this.sessionUiEvent.AD_PROGRESS, this.onAdProgress);
    session.removeEventListener(this.sessionUiEvent.AD_START, this.onAdStart);
    session.removeEventListener(this.sessionUiEvent.AD_END, this.onAdEnd);
    session.removeEventListener(this.sessionUiEvent.AD_CLICK, this.onAdClick);
    this.cleanupTrackingInfoListener();
  }

  private cleanupTrackingInfoListener(): void {
    if (!this.session) {
      return;
    }
    this.session.removeEventListener(this.sessionUiEvent.AD_TRACKING_INFO_RESPONSE, this.onTrackingInfo);
  }

  private handleAdBreakStart(adBreak: AdPlaybackState): void {
    const adData = adBreak.adObject.adData;

    const avails = this.convertToAvails([adData]);

    // Check playback policy for repeated ad breaks (linear playback)
    const currentAvail = avails[0];
    if (
      currentAvail &&
      this.adPolicyHandler &&
      this.adPolicyHandler.playbackRepeatedRule(currentAvail) !== true
    ) {
      // Skip this ad break - seek past it
      this.discardAdBreak(currentAvail);
      return;
    }

    /**
     *
     * Fire only before FIRST ad in the break
     */
    if (this.playerContext_ === PlayerContext.MAIN) {
      this.playerContext_ = PlayerContext.AD;
      this.currentAvail_ = avails[0];
      this.adBreakTotalAds = adData.availAdCount;

      this.currentAdBreakInfo_ = this.currentAvail_ && this.createAdBreakInfo(this.currentAvail_, this.adBreakTotalAds);

      this.eventEmitter.publish('playercontextchanged', this.playerContext_);

      // preroll load time
      if (this.currentAvail_?.startTimeInSeconds === 0) {
        this.prerollLoadTimeInMS = Number((performance.now() - this.playerLoadStartTimeInMS).toFixed(2));
      }

      this.eventEmitter.publish(AdEvent.ADVERT_BREAK_START, this.currentAdBreakInfo_);
    }

    /**
     * AD START (always per ad)
     */
    this.currentAdInfo = this.createAdInfo(adBreak);
    /**
     * Live MediaTailor may report an incomplete availAdCount while additional ads
     * are still being discovered. Update the expected total ad count to prevent
     * premature ADVERT_BREAK_END events and duplicate ad break reporting.
     */
    this.updateLiveAdBreakTotalAds(adData);
    this.quartileTracker.reset();
    this.eventEmitter.publish(AdEvent.ADVERT_START, this.currentAdInfo);
  }

  private discardAdBreak(currentAvail: Avail | undefined): void {
    if (this.adPolicyHandler && typeof this.adPolicyHandler.discardAdBreak === 'function') {
      this.adPolicyHandler.discardAdBreak(currentAvail);
      return;
    }
    if (currentAvail?.startTimeInSeconds !== undefined && currentAvail.durationInSeconds !== undefined) {
      this.player_.seek(currentAvail.startTimeInSeconds + currentAvail.durationInSeconds);
    }
  }

  private handleAdBreakEnd(adBreak: AdPlaybackState): void {
    const adData = adBreak.adObject.adData;

    const avails = this.convertToAvails([adData]);

    /**
     * AD END (always per ad)
     */
    this.currentAdInfo = this.createAdInfo(adBreak);
    this.eventEmitter.publish(AdEvent.ADVERT_END, this.currentAdInfo);

    /**
     * AD BREAK END
     * Fire only after LAST ad in the break
     */
    if (this.currentAdInfo.sequence === this.currentAdInfo.adBreakInfo?.totalAds) {
      this.playerContext_ = PlayerContext.MAIN;

      if (!this.player_.isLive) {
        this.adPolicyHandler?.setWatchedAdBreak(avails[0]);
        this.adPolicyHandler?.resolveSeekIntent(this.player_.currentTime);
        this.eventEmitter.publish(AdEvent.ADVERT_CUEPOINTS, this.getCuePointsRelativeToContentTime());
      }

      this.currentAvail_ = undefined;
      this.currentAdBreakInfo_ = this.createAdBreakInfo(avails[0], this.adBreakTotalAds);
      this.adBreakTotalAds = 0;

      this.eventEmitter.publish('playercontextchanged', this.playerContext_);
      this.eventEmitter.publish(AdEvent.ADVERT_BREAK_END, this.currentAdBreakInfo_);
    }
  }

  private createAdBreakInfo(avail: Avail, totalAds: number): AdBreakInfo {
    return {
      adBreakID: avail.availId ?? '',
      contentTimePosition: this.player_?.isLive
        ? AdContentTimePosition.MIDROLL
        : this.getAdPosition(avail?.startTimeInSeconds ?? 0, avail.durationInSeconds ?? 0),
      adBreakStartTimeOffsetMs: (avail?.startTimeInSeconds ?? 0) * 1000,
      durationMs: (avail?.durationInSeconds ?? 0) * 1000,
      totalAds: totalAds,
      remainingTimeMs: 0, // To be updated when ad progress events are handled
      adSequencePosition: 0,
      // Note: Check explicitly for 0 as it's a valid value for preroll ads
      adBreakIndex: avail.startTimeInSeconds !== undefined && avail.startTimeInSeconds === 0 ? 0 : totalAds - 1,
    };
  }

  private getAdClickTrackingURLs(ad: DatazoomMediaTailorAd): string[] {
    if (!ad.trackingEvents || !Array.isArray(ad.trackingEvents)) {
      return [];
    }
    const clickTrackingEvent = ad.trackingEvents.find(
      (trackingEvent: any) => trackingEvent.eventType === 'clickTracking',
    );
    return clickTrackingEvent?.beaconUrls ?? [];
  }
  private createAdInfo(ad: AdPlaybackState): AdInfo {
    const adData = ad.adObject?.adData;
    const countdown = ad.adCountdown;

    const startSec = adData?.start ?? 0;
    const durationSec = adData?.duration ?? 0;

    const rawSkipOffset = adData?.skipOffset;

    const isSkippable = typeof rawSkipOffset === 'number' && rawSkipOffset >= 0;

    const skipOffsetMs = isSkippable ? rawSkipOffset * 1000 : undefined;

    const liveSequence = Number.parseInt(adData?.adId?.split('_').pop() ?? '', 10);
    const adSequencePosition =
      this.player_?.isLive && !Number.isNaN(liveSequence) ? liveSequence : (adData?.availAdIndex ?? -1) + 1;
    return {
      adID: adData?.adId ?? '',
      sequence: adSequencePosition,
      adStartTimeOffsetMs: startSec * 1000,
      durationMs: durationSec * 1000,
      isSkippable,
      skipOffsetMs,
      isFiller: false,
      remainingTimeMs:
        typeof countdown?.toAdEnd === 'number' ? countdown.toAdEnd * 1000 : Math.max(durationSec * 1000, 0),
      adBreakInfo: this.currentAdBreakInfo_,
      vastProperties: {
        adSystem: adData?.adSystem ?? '',
        adTitle: adData?.adTitle ?? '',
        adServingId: adData?.vastAdId ?? '',
        creativeId: adData.creativeId ?? '',
        videoClickThroughURL: adData?.videoClickThrough ?? '',
        videoClickTrackingURLs: this.getAdClickTrackingURLs(adData) ?? [],
      },
      delivery: { technology: 'server-side', stitcher: 'AWS MediaTailor' },
    };
  }

  private getAdPosition(startTime: number, adDuration: number): AdContentTimePosition {
    const contentDuration = this.player_?.duration;

    if (startTime === 0) return AdContentTimePosition.PREROLL;

    if (contentDuration !== undefined && startTime + adDuration === contentDuration) {
      return AdContentTimePosition.POSTROLL;
    }

    return AdContentTimePosition.MIDROLL;
  }

  private reportAdProgress(ad: AdPlaybackState): void {
    if (!this.currentAdInfo) {
      return;
    }

    // Datazoom reports progress and duration in seconds.
    const progress = ad.adElapsedTime;
    const adDuration = ad.adObject?.adData?.duration ?? 0;
    const remainingTimeMs = ad.adCountdown.toAdEnd * 1000;

    this.currentAdInfo = {
      ...this.currentAdInfo,
      remainingTimeMs,
    };

    this.quartileTracker.emitQuartileEvents(progress, adDuration);

    this.eventEmitter.publish(AdEvent.ADVERT_PROGRESS_UPDATE, this.currentAdInfo, progress);
  }

  private readonly onProgressUpdate = (): void => {
    this.onManifestParsed();
  };

  private readonly streamEnded = (): void => {
    this.currentAvail_ = undefined;
    this.currentAdBreakInfo_ = undefined;
    this.currentAdInfo = undefined;
    this.adBreakTotalAds = 0;
    this.playerContext_ = PlayerContext.MAIN;
    this.eventEmitter.publish('playercontextchanged', this.playerContext_);
  };

  private readonly onManifestParsed = async (): Promise<void> => {
    this.player_?.unsubscribe('progressupdate', this.onProgressUpdate);
    if (this.player_.isLive) {
      this.adPolicyHandler = undefined;
      return;
    }

    this.adPolicyHandler?.setAdBreaks(this.avails);
    this.initializeWithInitialPlaybackTime();
    this.eventEmitter.publish(AdEvent.ADVERT_CUEPOINTS, this.getCuePointsRelativeToContentTime());
  };

  private readonly onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    switch (playbackState) {
      case PLAYBACK_STATES.LOADING:
        this.playerLoadStartTimeInMS = performance.now();
        break;
      case PLAYBACK_STATES.STARTED:
        break;
      case PLAYBACK_STATES.STOPPED:
      case PLAYBACK_STATES.IDLE:
        this.reset();
        break;
    }
  };

  private initializeWithInitialPlaybackTime(): void {
    if (!this.player_ || this.player_.isLive || !this.player_.playbackOptions?.initialPlaybackTime) {
      return;
    }

    const initialPlaybackTimeWithAds = this.getInitialPlaybackTimeWithAds();

    this.logger_?.log(`[MEDIATAILOR_ADS] overriding initial playback time`, initialPlaybackTimeWithAds);

    this.player_.seek(initialPlaybackTimeWithAds);
  }

  private reset(): void {
    this.resetState();
    this.unsubscribeToPlayerEvents();
    this.session && this.removeObserveAdEvents(this.session);
  }

  private resetState(): void {
    this.currentAdBreakInfo_ = undefined;
    this.currentAdInfo = undefined;
    this.playerContext_ = PlayerContext.MAIN;
    this.eventEmitter.publish('playercontextchanged', this.playerContext_);
  }
  private readonly onError = (error: Error): void => {
    if (this.playerContext_ !== PlayerContext.AD) {
      return;
    }
    this.eventEmitter.publish(AdEvent.ADVERT_ERROR, error, this.currentAdInfo);
  };

  private subscribeToPlayerEvents(): void {
    if (!this.player_) {
      return;
    }

    this.player_.subscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.player_.subscribe('manifestparsed', this.onManifestParsed);
    this.player_.subscribe('error', this.onError);
    this.player_.subscribe('streamended', this.streamEnded);
    /**
     * During validation on continue watching scenario on Apple platforms, it was observed that
     * seeking to initial playback time with ads was not working as expected if seek happens before the content is completely loaded.
     * This is similar issue that observed on seeking to initial playback time for live restart streams on Apple platforms.
     * To handle this properly, the onProgressUpdate handler is added to ensure seeking to initial playback time with ads
     * is handled correctly on continue watching scenarios.
     */
    if (PlatformUtil.isApple()) {
      this.player_.subscribe('progressupdate', this.onProgressUpdate);
    }
  }

  private unsubscribeToPlayerEvents(): void {
    if (!this.player_) {
      return;
    }

    this.player_.unsubscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.player_.unsubscribe('manifestparsed', this.onManifestParsed);
    this.player_.unsubscribe('error', this.onError);
    this.player_.unsubscribe('progressupdate', this.onProgressUpdate);
    this.player_.unsubscribe('streamended', this.streamEnded);
  }
  private getTotalAdvertsBreaksDuration(adBreaks: Avail[]): number {
    if (!adBreaks.length) {
      return 0;
    }
    return adBreaks
      .map((adBreak: Avail) => adBreak?.durationInSeconds ?? 0)
      .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);
  }

  private isCurrentAdLastInAdBreak(adData: DatazoomMediaTailorAd): boolean {
    const availDuration = adData.availDuration ?? 0;
    const completedDuration = (adData.inAvailStartOffset ?? 0) + (adData.duration ?? 0);

    return Math.round(completedDuration) >= Math.round(availDuration);
  }

  private updateLiveAdBreakTotalAds(adData: DatazoomMediaTailorAd): void {
    if (
      !this.player_.isLive ||
      this.isCurrentAdLastInAdBreak(adData) ||
      this.currentAdInfo?.sequence !== this.adBreakTotalAds
    ) {
      return;
    }

    this.adBreakTotalAds++;

    if (this.currentAdInfo.adBreakInfo) {
      this.currentAdInfo.adBreakInfo.totalAds = this.adBreakTotalAds;
    }
  }
}
